/** Pure functions for calculating product exclusivity rules. */

export type ProductName =
  | "general_liability"
  | "business_owners_policy"
  | "inland_marine"
  | "professional_liability";

export type DisabledReason =
  | "coverage_cannot_be_combined_with_BOP"
  | "coverage_is_included_with_BOP"
  | "buy_offline";

/** User-facing messages explaining why products are disabled. */
export const DISABLED_REASON_MESSAGES: Record<DisabledReason, string> = {
  coverage_cannot_be_combined_with_BOP:
    "This coverage cannot be combined with a Business Owner's Policy",
  coverage_is_included_with_BOP:
    "This coverage is included with the Business Owners Policy you selected",
  buy_offline:
    'If you would like to purchase these coverages together, one of our agents can assist at <a href="tel:8554801071">(855) 480-1071</a>',
};

type ProductExclusivityRule = {
  disables: {
    [product in ProductName]?: DisabledReason;
  };
};

export type ProductExclusivityRules = {
  [key in ProductName]?: ProductExclusivityRule;
};

export type DisabledByInfo = {
  product: ProductName;
  reason: DisabledReason;
};

/** Default rules defining which products disable others and why. */
export const DEFAULT_PRODUCT_EXCLUSIVITY_RULES: ProductExclusivityRules = {
  general_liability: {
    disables: {
      business_owners_policy: "coverage_cannot_be_combined_with_BOP",
    },
  },
  business_owners_policy: {
    disables: {
      inland_marine: "coverage_is_included_with_BOP",
      professional_liability: "buy_offline",
      general_liability: "coverage_is_included_with_BOP",
    },
  },
  inland_marine: {
    disables: {
      business_owners_policy: "coverage_cannot_be_combined_with_BOP",
      professional_liability: "buy_offline",
    },
  },
  professional_liability: {
    disables: {
      business_owners_policy: "buy_offline",
      inland_marine: "buy_offline",
    },
  },
};

/**
 * Calculates which products are disabled by selected products.
 * @returns Map of disabled products with reasons
 */
export function calculateDisabledProducts(
  selectedProducts: ProductName[],
): Map<ProductName, DisabledByInfo[]> {
  const disabledMap = new Map<ProductName, DisabledByInfo[]>();

  if (!selectedProducts.length) {
    return disabledMap;
  }

  selectedProducts.forEach(selectedProduct => {
    const rule = DEFAULT_PRODUCT_EXCLUSIVITY_RULES[selectedProduct];
    if (!rule) {
      return;
    }

    Object.entries(rule.disables).forEach(([productToDisable, reason]) => {
      const product = productToDisable as ProductName;
      const existingEntry = disabledMap.get(product);

      if (existingEntry) {
        // Product is already disabled by another selected product
        existingEntry.push({ product: selectedProduct, reason });
      } else {
        // First time this product is being disabled
        disabledMap.set(product, [{ product: selectedProduct, reason }]);
      }
    });
  });

  return disabledMap;
}

/** Returns disabled reasons for a product, or undefined if not disabled. */
export function isProductDisabled(
  product: ProductName,
  disabledProducts: Map<ProductName, DisabledByInfo[]>,
): DisabledByInfo[] | undefined {
  return disabledProducts.get(product);
}

/**
 * Returns the highest-priority disabled message.
 * Priority: cannot_be_combined > is_included > buy_offline
 */
export function getDisabledMessage(
  disabledByInfo: DisabledByInfo[],
): string | undefined {
  if (!disabledByInfo.length) {
    return undefined;
  }

  const reasons = disabledByInfo.map(info => info.reason);

  if (reasons.includes("coverage_cannot_be_combined_with_BOP")) {
    return DISABLED_REASON_MESSAGES.coverage_cannot_be_combined_with_BOP;
  } else if (reasons.includes("coverage_is_included_with_BOP")) {
    return DISABLED_REASON_MESSAGES.coverage_is_included_with_BOP;
  } else if (reasons.includes("buy_offline")) {
    return DISABLED_REASON_MESSAGES.buy_offline;
  }

  return undefined;
}

/**
 * Calculates disabled products with their user-facing messages.
 * @returns Map of disabled products to messages
 */
export function getDisabledProductsWithMessages(
  selectedProducts: ProductName[],
): Map<ProductName, string> {
  const disabledProducts = calculateDisabledProducts(selectedProducts);
  const result = new Map<ProductName, string>();

  disabledProducts.forEach((disabledByInfo, product) => {
    const message = getDisabledMessage(disabledByInfo);
    if (message) {
      result.set(product, message);
    }
  });

  return result;
}
